import { CityAttractionRO } from '@escapenavigator/types/dist/city-attraction/city-attraction.ro';
import { AttractionCategoryEnum } from '@escapenavigator/types/dist/city-attraction/enum/attraction-category.enum';
import { AttractionFameEnum } from '@escapenavigator/types/dist/city-attraction/enum/attraction-fame.enum';
import { AttractionReviewStatusEnum } from '@escapenavigator/types/dist/city-attraction/enum/attraction-review-status.enum';
import { Languages } from '@escapenavigator/types/dist/shared/enum/languages.enum';
import {
    Badge,
    Button,
    Center,
    Group,
    Image,
    Loader,
    Modal,
    ScrollArea,
    Select,
    Stack,
    Text,
    TextInput,
    UnstyledButton,
} from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { api } from '../api';
import { t } from '../dictionary';
import { categoryLabel, categoryOptions, fameLabel, fameOptions } from '../utils/attraction-labels';

type Props = {
    opened: boolean;
    onClose: () => void;
    cityId: number;
    language: string;
    onSelect: (attraction: CityAttractionRO) => void;
    onCreateNew: () => void;
};

const reviewBadge = (status: AttractionReviewStatusEnum) => {
    if (status === AttractionReviewStatusEnum.NEED_REVIEW) {
        return (
            <Badge size='xs' variant='light' color='yellow'>
                {t.stories.attractionNeedReview}
            </Badge>
        );
    }

    if (status === AttractionReviewStatusEnum.REJECTED) {
        return (
            <Badge size='xs' variant='light' color='red'>
                {t.stories.attractionRejected}
            </Badge>
        );
    }

    return null;
};

export const AttractionPicker = ({
    opened,
    onClose,
    cityId,
    language,
    onSelect,
    onCreateNew,
}: Props) => {
    const [searchText, setSearchText] = useState('');
    const [category, setCategory] = useState<string | null>(null);
    const [fame, setFame] = useState<string | null>(null);
    const [district, setDistrict] = useState('');
    const [items, setItems] = useState<CityAttractionRO[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!opened || !cityId) return;

        const timer = setTimeout(() => {
            setLoading(true);
            api.attractions({
                cityId,
                searchText: searchText.trim() || undefined,
                category: (category as AttractionCategoryEnum) || undefined,
                fame: (fame as AttractionFameEnum) || undefined,
                district: district.trim() || undefined,
            })
                .then(({ data }) => setItems(data))
                .catch(() => setItems([]))
                .finally(() => setLoading(false));
        }, 250);

        return () => clearTimeout(timer);
    }, [opened, cityId, searchText, category, fame, district]);

    useEffect(() => {
        if (!opened) {
            setSearchText('');
            setCategory(null);
            setFame(null);
            setDistrict('');
        }
    }, [opened]);

    const knownForOf = (a: CityAttractionRO) => {
        if (language === Languages.RU && a.knownForRu) return a.knownForRu;

        return a.knownFor || a.knownForRu;
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t.stories.pickAttraction}
            size='lg'
            centered
        >
            <Stack gap='md'>
                <TextInput
                    placeholder={t.stories.attractionSearchPlaceholder}
                    leftSection={<IconSearch size={16} />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.currentTarget.value)}
                />

                <Group grow align='flex-start'>
                    <Select
                        label={t.stories.attractionCategory}
                        clearable
                        searchable
                        placeholder={t.stories.allCategories}
                        data={categoryOptions}
                        value={category}
                        onChange={setCategory}
                    />
                    <Select
                        label={t.stories.attractionFame}
                        clearable
                        placeholder={t.stories.allFame}
                        data={fameOptions}
                        value={fame}
                        onChange={setFame}
                    />
                    <TextInput
                        label={t.stories.attractionDistrict}
                        placeholder={t.stories.attractionDistrictPlaceholder}
                        value={district}
                        onChange={(e) => setDistrict(e.currentTarget.value)}
                    />
                </Group>

                <Button variant='light' leftSection={<IconPlus size={16} />} onClick={onCreateNew}>
                    {t.stories.createAttraction}
                </Button>

                <ScrollArea.Autosize mah={360}>
                    {loading ? (
                        <Center py='xl'>
                            <Loader size='sm' />
                        </Center>
                    ) : !items.length ? (
                        <Text size='sm' c='dimmed' ta='center' py='xl'>
                            {t.stories.attractionNone}
                        </Text>
                    ) : (
                        <Stack gap='xs'>
                            {items.map((attraction) => {
                                const photo =
                                    attraction.photos?.[attraction.coverPhotoIndex ?? 0] ||
                                    attraction.photos?.[0];
                                const knownFor = knownForOf(attraction);

                                return (
                                    <UnstyledButton
                                        key={attraction.id}
                                        onClick={() => {
                                            onSelect(attraction);
                                            onClose();
                                        }}
                                        w='100%'
                                    >
                                        <Group
                                            wrap='nowrap'
                                            align='flex-start'
                                            p='sm'
                                            style={{
                                                border: '1px solid var(--mantine-color-gray-3)',
                                                borderRadius: 'var(--mantine-radius-md)',
                                            }}
                                        >
                                            {photo ? (
                                                <Image
                                                    src={photo}
                                                    w={56}
                                                    h={56}
                                                    radius='md'
                                                    fit='cover'
                                                    alt=''
                                                />
                                            ) : (
                                                <Center
                                                    w={56}
                                                    h={56}
                                                    style={{
                                                        borderRadius: 'var(--mantine-radius-md)',
                                                        background: 'var(--mantine-color-gray-1)',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <Text size='xs' c='dimmed'>
                                                        —
                                                    </Text>
                                                </Center>
                                            )}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <Group gap='xs' wrap='wrap'>
                                                    <Text fw={650} size='sm' truncate>
                                                        {attraction.title}
                                                    </Text>
                                                    {reviewBadge(attraction.reviewStatus)}
                                                </Group>
                                                <Text size='xs' c='dimmed' mt={2}>
                                                    {[
                                                        attraction.district,
                                                        fameLabel(attraction.fame),
                                                        categoryLabel(attraction.category),
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                </Text>
                                                {knownFor && (
                                                    <Text size='xs' mt={4} lineClamp={2}>
                                                        {knownFor}
                                                    </Text>
                                                )}
                                            </div>
                                        </Group>
                                    </UnstyledButton>
                                );
                            })}
                        </Stack>
                    )}
                </ScrollArea.Autosize>
            </Stack>
        </Modal>
    );
};
